import { ReactNode, useState, useEffect, useEffectEvent } from 'react';
import {
  Toast,
  ToastContainer,
  ToastProps as ToastComponentProps,
} from '@/components/ui/toast';
import { ToastContext, ToastProps } from './toast-context-definition';
import { registerToastBridge, unregisterToastBridge } from './toast-bridge';

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Omit<ToastComponentProps, 'onClose'>[]>(
    []
  );

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const toast = ({ title, description, variant = 'default' }: ToastProps) => {
    // Use timestamp + random value to ensure unique IDs even for rapid calls
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newToast: Omit<ToastComponentProps, 'onClose'> = {
      id,
      title,
      description,
      variant,
    };

    setToasts((prev) => {
      // Guard against the same message being toasted twice for one failure
      // (e.g. a generic mutation-level toast plus a form-level fallback toast).
      const isDuplicate = prev.some(
        (t) =>
          t.title === title &&
          t.description === description &&
          t.variant === variant
      );
      return isDuplicate ? prev : [...prev, newToast];
    });
  };

  const onBridgeToast = useEffectEvent((props: ToastProps) => {
    toast(props);
  });

  useEffect(() => {
    registerToastBridge(onBridgeToast);
    return () => unregisterToastBridge(onBridgeToast);
  }, []);

  return (
    <ToastContext value={{ toast }}>
      {children}
      <ToastContainer>
        {toasts.map((toastItem) => (
          <Toast key={toastItem.id} {...toastItem} onClose={removeToast} />
        ))}
      </ToastContainer>
    </ToastContext>
  );
}
